import { Checkbox, Form, Input, Select, InputNumber, message } from "antd";
import { useGetBrandsQuery } from "../../../redux/services/brandApi";
import { Rule } from "antd/es/form";
import { useGetUsersQuery } from "../../../redux/services/userApi";
import { useGetLocationsQuery } from "../../../redux/services/locationsApi";
import { customerCityOptions } from "../../../constants/locationConstants";

const { Option } = Select;

interface InputProps {
  disabled?: boolean;
  label: string;
  name: string;
  rules?: Rule[];
}

export const InputElement = (props: InputProps) => {
  const { disabled, label, name, rules } = props;
  return (
    <Form.Item label={label} name={name} rules={rules ? rules : []}>
      <Input disabled={disabled} />
    </Form.Item>
  );
};

export const NumberInputElement = (props: InputProps) => {
  const { disabled, label, name, rules } = props;
  return (<Form.Item label={label} name={name} rules={rules ? rules : []}>
    <InputNumber
      disabled={disabled}
      style={{ width: "100%" }}
      min={0}
      step={0.1}
      placeholder="Enter distance"
      onKeyDown={(e) => {
        const allowedKeys = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
          "Home",
          "End",
          ".",
        ];

        if (
          !/[0-9]/.test(e.key) &&
          !allowedKeys.includes(e.key)
        ) {
          e.preventDefault();
          message.warning("Please enter only numbers or decimal values");
        }
      }}
    />
  </Form.Item>)
}

interface BrandSelectProps {
  disabled?: boolean;
  onSelect: any;
}

export const BrandSelect = (props: BrandSelectProps) => {
  const { disabled, onSelect } = props;

  const { data: brandData } = useGetBrandsQuery({ all_pages: true });
  const brands: any = brandData?.brands || [];

  return (
    <Form.Item label="Brand" name="brand_id">
      <Select
        placeholder="Select brand"
        allowClear
        showSearch
        disabled={disabled}
        onSelect={onSelect}
        optionFilterProp="children"
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {brands?.map((brand: any) => (
          <Option key={brand?.name} value={brand?.id}>
            {brand?.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

interface BrandModelSelectProps {
  disabled?: boolean;
  brandModels: [];
}

export const BrandModelSelect = (props: BrandModelSelectProps) => {
  const { disabled, brandModels } = props;

  return (
    <Form.Item label="Brand Model" name="brand_model_id">
      <Select
        placeholder="Select brand model"
        allowClear
        showSearch
        disabled={disabled}
        filterOption={(inputValue, option: any) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        }
      >
        {brandModels?.map((model: any) => (
          <Option key={model.name} value={model.id}>
            {model.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

interface CityManagerSelectProps {
  disabled?: boolean;
  required?: boolean;
}

export const CityManagerSelect = (props: CityManagerSelectProps) => {
  const { disabled, required = true, } = props;
  const { data: cityManagerData } = useGetUsersQuery({
    role_type: "city_manager",
    all_pages: true
  });
  const cityManagers = cityManagerData?.users || [];

  return (
    <Form.Item
      label="Assigned City Manager"
      name="assigned_city_manager_id"
      rules={[{ required: required, message: "Please select city manager" }]}
    >
      <Select
        placeholder="Select city manager"
        allowClear
        showSearch
        disabled={disabled}
        optionFilterProp="children"
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {cityManagers?.map((manager: any) => (
          <Option key={manager?.id} value={manager?.id}>
            {manager?.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

interface CheckBoxInputProps {
  disabled?: boolean;
  label: string;
  name: string;
}

export const CheckBoxInput = (props: CheckBoxInputProps) => {
  const { label, name, disabled } = props;
  return (
    <Form.Item label={label} name={name} valuePropName="checked">
      <Checkbox>Tick if Yes</Checkbox>
    </Form.Item>
  );
};

interface SelectProps {
  disabled?: boolean;
  required?: boolean;
  onChange?: any;
}

export const ZoneSelect = (props: SelectProps) => {
  const { disabled, required } = props;

  const { data: zoneLocationsData } = useGetLocationsQuery({
    location_type: "zone",
    all_pages: true
  });
  const zones = zoneLocationsData?.locations;

  return (
    <Form.Item
      label="Zone"
      name="zone_id"
      rules={[{ required: required, message: "Please select zone" }]}
    >
      <Select
        placeholder="Select zone"
        allowClear
        showSearch
        optionFilterProp="children"
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        disabled={disabled}
      >
        {zones?.map((zone: any) => (
          <Option key={zone.name} value={zone.id}>
            {zone.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

interface StateSelectProps {
  disabled?: boolean;
  required?: boolean;
  onChange?: any;
  label?: string;
  placeholder?: string;
}

export const StateSelect = (props: StateSelectProps) => {
  const { disabled, onChange, required, label = "State", placeholder = "Select State" } = props;

  const { data: stateLocationsData } = useGetLocationsQuery({
    location_type: "state",
    all_pages: true
  });
  const states = stateLocationsData?.locations;

  return (
    <Form.Item
      label={label}
      name="state_id"
      rules={[{ required: required, message: "Please select state" }]}
    >
      <Select
        placeholder={placeholder}
        allowClear
        showSearch
        optionFilterProp="children"
        disabled={disabled}
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {states?.map((state: any) => (
          <Option key={state.name} value={state.id}>
            {state.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

interface CitySelectProps {
  disabled?: boolean;
  required?: boolean;
  onChange?: any;
  label?: string;
  placeholder?: string
}
export const CitySelect = (props: CitySelectProps) => {
  const { disabled, onChange, label = "EV City", placeholder = "Select EV city" } = props;

  const { data: cityLocationsData } = useGetLocationsQuery({
    location_type: "city",
    all_pages: true
  });
  const cities = cityLocationsData?.locations;

  return (
    <Form.Item label={label} name="city_id">
      <Select
        placeholder={placeholder}
        allowClear
        showSearch
        onChange={onChange}
        disabled={disabled}
        optionFilterProp="children"
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {cities?.map((city: any) => (
          <Option key={city.name} value={city.id}>
            {city.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export const CustomerCitySelect = (props: CitySelectProps) => {
  const { disabled } = props;
  return (
    <Form.Item label="Customer City" name="customer_city">
      <Select
        placeholder="Select customer city"
        allowClear
        showSearch
        disabled={disabled}
        optionFilterProp="children"
        filterOption={(inputValue, option: any) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        }
        options={customerCityOptions}
      ></Select>
    </Form.Item>
  );
};

export const PincodeInputElement = (props: SelectProps) => {
  const { disabled } = props;
  return (
    <InputElement
      label="Pincode"
      name="pincode"
      disabled={disabled}
      rules={[
        {
          required: true,
          pattern: /^[1-9][0-9]{5}$/,
          message: "Please Enter Valid Pincode"
        }
      ]}
    />
  );
};

export const ContactNumberInputElement = (props: SelectProps) => {
  const { disabled } = props;
  return (
    <InputElement
      label="Contact Number"
      name="contact_number"
      disabled={disabled}
      rules={[
        { required: true, min: 10, message: "Contact Number must be minimum 10 digit." },
        { max: 10, message: "Contact Number be maximum 10 digit." },
        { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
      ]}
    />
  );
};

export const AltContactNumberInputElement = (props: SelectProps) => {
  const { disabled } = props;
  return (
    <InputElement
      label="Alt Contact Number"
      name="alt_contact_number"
      disabled={disabled}
      rules={[
        { min: 10, message: "Contact Number must be minimum 10 digit." },
        { max: 10, message: "Contact Number be maximum 10 digit." },
        { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
      ]}
    />
  );
};

interface DropDownSelectProps {
  disabled?: boolean;
  label: string;
  name: string;
  placeHolder?: string;
  records: [];
  onSelect?: any;
  required?: boolean;
}

export const DropDownSelectProps = (props: DropDownSelectProps) => {
  const { disabled, label, name, records, placeHolder, onSelect, required } = props;

  return (
    <Form.Item
      label={label}
      name={name}
      rules={[{ required: required, message: "Please select partner" }]}
    >
      <Select
        placeholder={placeHolder}
        allowClear
        showSearch
        onSelect={
          onSelect
            ? onSelect
            : () => {
              console.log("");
            }
        }
        optionFilterProp="children"
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {records?.map((record: any) => (
          <Option key={record?.id} value={record?.id}>
            {record?.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};
